import { useEffect, useState, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Search, Download, Mail, Phone, ArrowLeft, Users } from 'lucide-react';
import { useAuth, authHeaders } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSubscribers() {
  const { user, loading, logout } = useAuth();
  const [subs, setSubs] = useState([]);
  const [query, setQuery] = useState('');
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/subscribers`, { headers: authHeaders() })
      .then(r => setSubs(r.data))
      .catch(e => { if (e?.response?.status === 401) logout(); })
      .finally(() => setLoadingList(false));
  }, [user, logout]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter(s =>
      (s.email || '').toLowerCase().includes(q) ||
      (s.name || '').toLowerCase().includes(q) ||
      (s.phone || '').toLowerCase().includes(q)
    );
  }, [subs, query]);

  const exportCsv = () => {
    const rows = [
      ['Name', 'Email', 'Phone', 'Source', 'Consented at', 'Lead ID'],
      ...filtered.map(s => [s.name || '', s.email || '', s.phone || '', s.source || '', s.consented_at || '', s.lead_id || '']),
    ];
    const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `larae-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="min-h-[50vh] grid place-items-center font-manrope text-ink/60">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <>
      <SEO title="Marketing Subscribers | Laraé Logistics Admin" description="Internal." />
      <div className="bg-cream min-h-[calc(100vh-160px)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-10">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Link to="/admin" className="text-ink/60 hover:text-forest" aria-label="Back to dashboard"><ArrowLeft size={20} /></Link>
              <div>
                <h1 className="font-outfit text-3xl font-medium text-ink">Marketing Subscribers</h1>
                <p className="font-manrope text-sm text-ink/60 mt-1">{user.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/admin" className="text-sm font-manrope text-ink/70 hover:text-forest px-4 py-2 rounded-full border border-sand">Leads</Link>
              <button data-testid="subs-logout-btn" onClick={logout} className="text-sm font-manrope text-white bg-forest px-4 py-2 rounded-full inline-flex items-center gap-2"><LogOut size={14} /> Logout</button>
            </div>
          </div>

          {/* Stats + actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-forest text-white rounded-2xl p-5">
              <div className="font-outfit text-3xl font-semibold flex items-center gap-2"><Users size={22} /> {subs.length}</div>
              <div className="font-manrope text-xs mt-1 text-white/80">Total subscribers</div>
            </div>
            <div className="bg-white border border-sand rounded-2xl p-5">
              <div className="font-outfit text-3xl font-semibold text-ink">{filtered.length}</div>
              <div className="font-manrope text-xs mt-1 text-ink/60">Showing (after search)</div>
            </div>
            <button data-testid="subs-export-csv" onClick={exportCsv} disabled={!filtered.length}
              className="bg-oat hover:bg-oat/90 text-ink font-semibold rounded-2xl p-5 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5">
              <Download size={18} /> Export to CSV
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-5">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
            <input data-testid="subs-search-input" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search by name, email or phone…"
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-sand bg-white text-ink placeholder:text-ink/40 focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 text-sm" />
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-sand overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-manrope">
                <thead className="bg-sand/60 text-ink">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Name</th>
                    <th className="text-left px-5 py-3 font-medium">Email</th>
                    <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Phone</th>
                    <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Source</th>
                    <th className="text-left px-5 py-3 font-medium">Consented</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingList ? (
                    <tr><td colSpan={5} className="text-center py-10 text-ink/50">Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-10 text-ink/50">
                      {query ? 'No subscribers match your search.' : 'No subscribers yet. Customers who opt-in on the quote form will appear here.'}
                    </td></tr>
                  ) : filtered.map(s => (
                    <tr key={s.email} className="border-t border-sand hover:bg-sand/30 transition-colors">
                      <td className="px-5 py-4 font-medium text-ink">{s.name || '—'}</td>
                      <td className="px-5 py-4 text-ink/80"><a href={`mailto:${s.email}`} className="hover:text-forest inline-flex items-center gap-1.5"><Mail size={13} className="text-ink/40" /> {s.email}</a></td>
                      <td className="px-5 py-4 text-ink/70 hidden md:table-cell"><a href={`tel:${s.phone}`} className="hover:text-forest inline-flex items-center gap-1.5"><Phone size={13} className="text-ink/40" /> {s.phone || '—'}</a></td>
                      <td className="px-5 py-4 text-ink/60 hidden md:table-cell text-xs capitalize">{(s.source || '—').replace('_', ' ')}</td>
                      <td className="px-5 py-4 text-ink/60 text-xs">{s.consented_at ? new Date(s.consented_at).toLocaleString('en-GB') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
